import React, { useState, useEffect, useMemo, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '../context/FormContext';
import FormStepLayout from '../components/FormStepLayout';
import FieldError from '../components/FieldError';
import { validateAdditionalInfoOptional } from '../utils/fieldValidation';
import '../styles/theme.css';
import '../styles/form.css';
import '../styles/form-step.css';

const ADDITIONAL_INFO_MAX_LEN = 10000;

const AdditionalInfoForm = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const {
    formData,
    updateFormData,
    ensureDraft,
    persistDraft,
    clearSessionAfterApplicationComplete,
    saveError,
    isSaving,
    draftReady,
  } = useForm();
  const [additional_information, setAdditionalInformation] = useState(
    formData.additional_information ?? ''
  );
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    void ensureDraft();
  }, [ensureDraft]);

  useEffect(() => {
    if (!draftReady) return;
    setAdditionalInformation(formData.additional_information ?? '');
  }, [draftReady, formData.additional_information]);

  const additionalInfoError = useMemo(
    () => validateAdditionalInfoOptional(additional_information, ADDITIONAL_INFO_MAX_LEN),
    [additional_information]
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (additionalInfoError) return;
    const next = { ...formData, additional_information };
    updateFormData(next);
    try {
      await persistDraft(next, 'complete');
      clearSessionAfterApplicationComplete();
      setSubmitted(true);
    } catch {
      /* saveError in context */
    }
  };

  if (submitted) {
    return (
      <FormStepLayout
        title={t('forms.step3.thankYouTitle')}
        currentStep={3}
        totalSteps={3}
        showNext={false}
      >
        <p style={{ marginTop: 0 }}>{t('forms.step3.thankYouBody')}</p>
      </FormStepLayout>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {saveError && (
        <div role="alert" className="toast toast--error" style={{ marginBottom: '1rem' }}>
          {t(saveError)}
        </div>
      )}
      <FormStepLayout
        title={t('forms.step3.title')}
        currentStep={3}
        totalSteps={3}
        nextLabel={t('forms.submit')}
        submitButton
        nextDisabled={Boolean(additionalInfoError) || isSaving}
      >
        <div
          className={`form-field ${additional_information ? 'has-value' : ''} ${
            additionalInfoError ? 'form-field--error' : ''
          }`}
        >
          <textarea
            className="form-input form-textarea"
            value={additional_information}
            onChange={(e) => setAdditionalInformation(e.target.value)}
            placeholder={t('forms.step3.placeholder')}
            rows={5}
            aria-label={t('forms.step3.ariaLabel')}
            aria-invalid={Boolean(additionalInfoError)}
          />
          <FieldError
            message={additionalInfoError}
            values={
              additionalInfoError
                ? { max: ADDITIONAL_INFO_MAX_LEN.toLocaleString(i18n.language) }
                : undefined
            }
          />
        </div>
      </FormStepLayout>
    </form>
  );
};

export default AdditionalInfoForm;
